'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import type { Reference } from '@eliadmin/core/src/api/references/references-types';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Paragraph } from '../ui/paragraph';
import { Heading } from '../ui/heading';
import { Button } from '../ui/button';
import { GoArrowUpRight } from 'react-icons/go';
import { useState } from 'react';
import { useDisclosure } from '@eliadmin/core/src/hooks/use-disclosure';
import { LightboxComponent } from '@eliadmin/core/src/components/lightbox/lightbox-component';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

export function ReferenceCard({ reference }: { reference: Reference }) {
  const { t } = useTranslations();
  const { isOpen, setIsOpen } = useDisclosure(false);
  const [imagesSorted, setImagesSorted] = useState<{ url: string; name: string }[]>([]);

  const handleOpenGallery = (images: any, index: number) => () => {
    if (Array.isArray(images) && index >= 0 && index < images.length) {
      const newImages = [...images.slice(index), ...images.slice(0, index)];
      setImagesSorted(newImages);
      setIsOpen(true);
    } else if (images && !Array.isArray(images)) {
      setImagesSorted([images]);
      setIsOpen(true);
    }
  };

  const allImages = [reference?.image, ...(reference?.images ?? [])].filter(Boolean);

  return (
    <>
      <LightboxComponent isOpen={isOpen} setIsOpen={setIsOpen} images={imagesSorted} />
      <div
        className={cn(
          'custom-container',
          'flex w-full flex-col items-start overflow-hidden rounded-[16px] bg-white md:flex-row md:items-stretch',
        )}
      >
        <div className="flex w-full flex-col items-start justify-between gap-16 p-4 sm:p-6 md:max-w-[800px] lg:gap-[72px] lg:p-8 xl:p-12">
          <div className="flex w-full flex-col items-start justify-start gap-6 lg:gap-8">
            <div className="flex w-full flex-wrap items-start justify-start gap-2">
              {!!reference.year && (
                <Paragraph
                  size="sm"
                  color="black"
                  className={cn(
                    'flex h-[30px] items-center justify-center rounded-full border border-border px-4 text-sm -tracking-[4%] text-black sm:h-8',
                  )}
                >
                  {reference.year}
                </Paragraph>
              )}
              {reference?.categories &&
                reference.categories.length > 0 &&
                reference.categories.map(category => (
                  <Paragraph
                    key={category.name}
                    size="sm"
                    color="black"
                    className={cn(
                      'flex h-[30px] items-center justify-center rounded-full border border-border px-4 text-sm -tracking-[4%] text-black sm:h-8',
                    )}
                  >
                    {category.name}
                  </Paragraph>
                ))}
            </div>
            <Heading title={reference.name} tag="h3" className={{ title: '-tracking-[6%]' }} />
            <Paragraph
              innerHtml={reference.description}
              color="muted-foreground"
              className="line-clamp-1 w-full -tracking-[6%]"
              size="lg"
            />
          </div>
          <Button variant={'outline'} onClick={handleOpenGallery(allImages, 0)}>
            {t('text2367')} <GoArrowUpRight size={20} />
          </Button>
        </div>
        <Image
          src={allImages[0]?.url ?? '/images/placeholder.png'}
          alt={resolveImageAlt(allImages?.[0], reference?.name)}
          width={0}
          height={0}
          className="h-[233px] w-full object-cover object-center grayscale sm:h-auto md:max-w-[400px] lg:max-w-[600px]"
          sizes="100vw"
        />
      </div>
    </>
  );
}
